import './ActivateMembershipModal.css';

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useActivateMembershipRequest } from '../queries';
import type { MembershipRequestRow } from '../api/membership.api';
import type { ApiError } from '../../../shared/api/apiError';

type Props = {
  open: boolean;
  request: MembershipRequestRow | null;
  onClose: () => void;
};

const extractError = (err: unknown): string => {
  const apiErr = err as ApiError | undefined;
  const body = apiErr?.data as { message?: string } | undefined;
  return body?.message || apiErr?.message || 'Could not activate. Check the code and try again.';
};

const ActivateMembershipModal = ({ open, request, onClose }: Props) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const activate = useActivateMembershipRequest();

  // Reset transient state whenever the modal opens for a (new) request.
  useEffect(() => {
    if (open) {
      setCode('');
      setError(null);
    }
  }, [open, request?.requestUuid]);

  if (!open || !request) return null;

  const canSubmit = code.length === 6 && !activate.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      await activate.mutateAsync({ requestUuid: request.requestUuid, code });
      onClose();
    } catch (err) {
      setError(extractError(err));
    }
  };

  return (
    <div className="activate-modal__overlay" role="dialog" aria-modal="true" aria-label="Activate membership">
      <div className="activate-modal">
        <header className="activate-modal__header">
          <h3 className="activate-modal__title">Activate membership</h3>
          <button type="button" className="activate-modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <p className="activate-modal__context">
          <strong>{request.customerName}</strong>
          {request.customerPhoneMasked ? <span> · {request.customerPhoneMasked}</span> : null}
          <br />
          {request.planName}
        </p>

        <label className="activate-modal__label" htmlFor="activate-code">
          Enter the 6-digit code the customer reads out
        </label>
        <input
          id="activate-code"
          className="activate-modal__input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          value={code}
          autoFocus
          onChange={(e) => {
            setError(null);
            setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSubmit();
          }}
        />

        {error ? <p className="activate-modal__error" role="alert">{error}</p> : null}

        <footer className="activate-modal__footer">
          <button type="button" className="activate-modal__btn activate-modal__btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="activate-modal__btn activate-modal__btn--primary"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {activate.isPending ? 'Activating…' : 'Activate'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ActivateMembershipModal;
